// The build-time content path (spec ruling R4), as the app sees it.
//
// These imports resolve to content/, which is GENERATED by
// scripts/generate-content.mjs and gitignored. That is deliberate: if the JSON
// were committed, a stale copy could ship, which is the "drift" risk ADR-0002
// names explicitly.
//
// If one of these files is missing the build fails here, loudly, at import
// resolution — there is no fallback object and there must never be one. A site
// that renders an empty page because content was missing is exactly the
// "blank page" failure spec §9 asks the generator to make impossible.
//
// npm's predev / prebuild / pretest hooks all run the generator first, so the
// only way to hit that failure is to run vite directly.

import build from '../content/build.json'
import commands from '../content/commands.json'
import docsCards from '../content/docs-cards.json'
import docsEvents from '../content/docs-events.json'
import docsHoops from '../content/docs-hoops.json'
import docsMovieNight from '../content/docs-movie-night.json'
import docsMusic from '../content/docs-music.json'
import docsWatch from '../content/docs-watch.json'
import documentation from '../content/documentation.json'
import explainer from '../content/explainer.json'
import landing from '../content/landing.json'
import releases from '../content/releases.json'
import sections from '../content/sections.json'

export {
  build,
  commands,
  docsCards,
  docsEvents,
  docsHoops,
  docsMovieNight,
  docsMusic,
  docsWatch,
  documentation,
  explainer,
  landing,
  releases,
  sections,
}

/** The section registry, flattened — one source for routes AND navigation, so
 * the two cannot disagree about what pages exist. */
export const sectionList = sections.sections

/**
 * The sections that appear in the nav and the tab bar — P6-104.
 *
 * 🔴 THE NAV IS TOP-LEVEL ONLY, AND THAT IS THE POINT OF THE RESTRUCTURE. Jordan
 * asked for five items — Home · Docs · Commands · Trips · Releases — and for the
 * documentation to be a page per feature underneath. Those two only coexist
 * because a child is filtered out here: a nav built from the whole registry
 * would have grown a row every time a feature page landed, which is the shape
 * this row exists to replace.
 *
 * Layout.jsx filters this by TIER as well, so a crew section is not advertised
 * to somebody who cannot open it. Two separate rules, deliberately not merged —
 * one is about structure and one is about visibility.
 */
export const topLevelSections = sectionList.filter((section) => !section.parent)

/**
 * The pages beneath one section, in registry order.
 *
 * Order comes from the file rather than from a sort, because the generator
 * requires a parent to be declared above its children — so "the order they are
 * written in" is a real, reviewable decision rather than an accident of
 * alphabetising `docs-events` above `docs-movie-night`.
 */
export function childSections(parentId) {
  return sectionList.filter((section) => section.parent === parentId)
}

/**
 * Every authored prose document, keyed by the content-src file it came from.
 *
 * 🔴 This exists for ONE consumer and it is a test: src/test/documentation.test.jsx
 * walks all of them looking for a `/command` that Discord would answer with
 * nothing. Before P6-104 it walked a hand-written list of three, and a fourth
 * page would have been unwatched prose the day it landed — which is precisely
 * how docs/ came to carry 69 dead command names.
 *
 * Built from the imports above rather than from a glob: a glob that matched
 * nothing would leave the check walking an empty set and passing forever.
 */
export const proseDocuments = {
  documentation,
  explainer,
  'docs-movie-night': docsMovieNight,
  'docs-events': docsEvents,
  'docs-hoops': docsHoops,
  'docs-music': docsMusic,
  'docs-watch': docsWatch,
  'docs-cards': docsCards,
}

/**
 * The generated "commands" block of one feature's docs page: a `[invocation,
 * description]` pair for every catalog command of `feature`, in the catalog's
 * own order. DR2 asks every feature page to carry its commands, generated.
 *
 * 🔴 ONE HELPER, TWO PAGES. This was two functions — `watchCommandItems` in
 * routes/DocsWatch.jsx (P6-302) and `cardsCommandItems` in routes/DocsCards.jsx
 * (P6-305) — the same four lines with a different feature name. The second was
 * written as a copy because the first was not its lane's to edit, and its report
 * said the next page to want one should lift both here. P6-312 did. The feature
 * name is the only thing a page supplies, so a third page is one call and not a
 * third copy.
 *
 * It lives here rather than in scripts/generate-content.mjs on purpose: teaching
 * the SHARED generator a per-page feature filter would make every sibling page
 * opt out of a shape only a few pages use, and the generator never sees which
 * page is asking.
 *
 * Takes the catalog as an argument instead of reading `commands.commands`
 * itself — the "inject the input" shape `portal/branches.js::branchesFor` also
 * uses — so a test can hand it a row the real catalog does not carry. That is
 * the only way to prove the block is generated and not a hand-typed list beside
 * a comment that says otherwise (src/test/documentation.test.jsx).
 */
export function featureCommandItems(allCommands, feature) {
  return allCommands
    .filter((command) => command.feature === feature)
    .map((command) => [command.invocation, command.description])
}
